/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useRef } from 'react';
import {
  VStack,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  HStack,
  Text,
  Spinner,
} from '@chakra-ui/react';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import ImageUpload from '@/components/common/ImageUpload';
import { AddressFieldWithMap } from '@/components/common/AddressFieldWithMap';
import CustomSelect from '@/components/common/CustomSelect';
import { useGetCountries, useGetGenders } from '@/services/account-setting';
import Asterisk from '@/components/common/Asterisk';
import FormModal from '@/components/common/FormModal'; // Import the reusable FormModal
import { useGoogleMaps } from '@/hooks/useGoogleMaps';

type FormData = {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  gender: string;
  country: string;
  address: string;
  photo: File | string | null;
};

const accountValidationSchema = yup.object().shape({
  first_name: yup
    .string()
    .required('First name is required')
    .min(2, 'First name must be at least 2 characters')
    .max(50, 'First name cannot exceed 50 characters'),
  last_name: yup
    .string()
    .required('Last name is required')
    .min(2, 'Last name must be at least 2 characters')
    .max(50, 'Last name cannot exceed 50 characters'),
  email: yup
    .string()
    .email('Please enter a valid email address')
    .required('Email address is required'),
  phone: yup
    .string()
    .optional()
    .nullable()
    .test('phone-validation', 'Please enter a valid phone number', (value) => {
      if (!value) return true; // Optional field
      return /^[0-9+\-\s()]+$/.test(value) && value.length >= 10;
    }),
  gender: yup.string().optional().nullable(),
  country: yup.string().optional().nullable(),
  address: yup.string().optional().nullable().max(200, 'Address cannot exceed 200 characters'),
  photo: yup
    .mixed()
    .nullable()
    .optional()
    .test('file-size', 'Image file size must be less than 1MB', (value) => {
      if (!value || typeof value === 'string') return true;
      if (value instanceof File) return value.size <= 1024 * 1024;
      return true;
    })
    .test('file-type', 'Only JPG, PNG images are allowed', (value) => {
      if (!value || typeof value === 'string') return true;
      if (value instanceof File) {
        return ['image/jpeg', 'image/png', 'image/jpg'].includes(value.type);
      }
      return true;
    }),
});

type IAccountEditModalProps = {
  isOpen: boolean;
  onClose: () => void;
  handleUpdate: (data: FormData) => void;
  handleCancel: () => void;
  initialData: Partial<FormData> & { photoUrl?: string };
};

const AccountEditModal: React.FC<IAccountEditModalProps> = ({
  isOpen,
  onClose,
  handleUpdate,
  initialData,
}) => {
  const methods = useForm<FormData>({
    resolver: yupResolver(accountValidationSchema) as any,
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      gender: '',
      country: '',
      address: '',
      photo: null,
    },
  });

  const initialValuesRef = useRef<Partial<FormData>>({});

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    reset,
  } = methods;

  const { data: countriesData, isLoading: countriesLoading } = useGetCountries();
  const { data: gendersData, isLoading: gendersLoading } = useGetGenders();

  // Memoize genderOptions and countryOptions to prevent recalculation
  const genderOptions = useMemo(
    () =>
      gendersData
        ? gendersData.map((gender: any) => ({
            id: gender.id.toString(),
            label: gender.gender_type,
            searchLabel: gender.gender_type,
          }))
        : [],
    [gendersData],
  );

  const countryOptions = useMemo(() => {
    if (!countriesData || !(countriesData as any).results) return [];
    return (countriesData as any).results.map((country: any) => ({
      id: country.id.toString(),
      label: country.country_name,
      searchLabel: country.country_name,
    }));
  }, [countriesData]);

  useEffect(() => {
    if (isOpen && initialData) {
      reset({
        first_name: initialData.first_name || '',
        last_name: initialData.last_name || '',
        email: initialData.email || '',
        phone: initialData.phone || '',
        gender: initialData.gender || '', // ← directly use string
        country: initialData.country || '', // ← directly use string
        address: initialData.address || '',
        photo: null,
      });

      // Store initial for change detection
      initialValuesRef.current = {
        first_name: initialData.first_name || '',
        last_name: initialData.last_name || '',
        email: initialData.email || '',
        phone: initialData.phone || '',
        gender: initialData.gender || '',
        country: initialData.country || '',
        address: initialData.address || '',
        photo: initialData.photoUrl || null,
      };
    }

    if (!isOpen) {
      reset();
      initialValuesRef.current = {};
    }
  }, [isOpen, initialData, reset]);

  const { isLoaded: isGoogleMapsLoaded } = useGoogleMaps(isOpen);

  // Function to detect changed fields for PATCH request
  const getChangedFields = (currentValues: FormData) => {
    const changedFields: any = {};
    const initialValues = initialValuesRef.current;

    Object.keys(currentValues).forEach((key) => {
      const currentValue = currentValues[key as keyof FormData];
      const initialValue = initialValues[key as keyof FormData];

      // Handle special cases
      if (key === 'photo') {
        if (currentValue instanceof File) {
          changedFields.photo = currentValue;
        } else if (currentValue === null && initialValue !== null) {
          // Optional: send null to remove photo
          changedFields.photo = null;
        }
        return;
      }
      if (key === 'gender' || key === 'country') {
        const currentNum = currentValue ? parseInt(currentValue as string) : null;
        const initialNum = initialValue ? parseInt(initialValue as string) : null;
        if (currentNum !== initialNum) {
          changedFields[key] = currentNum;
        }
        return;
      }

      // For other fields, compare values
      const cleanCurrent = currentValue || null;
      const cleanInitial = initialValue || null;

      if (cleanCurrent !== cleanInitial) {
        changedFields[key] = cleanCurrent;
      }
    });

    return changedFields;
  };

  const onSubmit = async (data: FormData) => {
    const changedFields = getChangedFields(data);

    if (Object.keys(changedFields).length === 0) {
      handleClose();
      return;
    }

    try {
      await handleUpdate(changedFields); // ← wait for success
      reset(); // ← now safe to reset
      handleClose();
    } catch (error) {
      // handleUpdate already shows toast
      console.error('Update failed:', error);
    }
  };

  const handleClose = () => {
    reset();
    initialValuesRef.current = {};
    onClose();
  };

  const enhancedInitialData = useMemo(() => {
    const photoUrl =
      initialData.photoUrl ?? (typeof initialData.photo === 'string' ? initialData.photo : null);
    return {
      ...initialData,
      photoUrl: photoUrl as string | null,
    };
  }, [initialData]);

  return (
    <FormProvider {...methods}>
      <FormModal
        isOpen={isOpen}
        onClose={handleClose}
        title="Edit Account Details"
        size="xl"
        isCentered={true}
        showCloseButton={true}
        onConfirm={handleSubmit(onSubmit)}
        isLoading={methods.formState.isSubmitting}
        saveText="Update"
      >
        <VStack spacing={6} align="stretch">
          <Controller
            name="photo"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormControl isInvalid={!!error}>
                <FormLabel
                  color={error ? 'status.error' : 'text.secondary'}
                  fontSize={'sm'}
                  fontFamily={'primary'}
                  fontWeight={'normal'}
                  mb={'6px'}
                >
                  Profile Photo
                </FormLabel>
                <ImageUpload
                  htmlFor="photo"
                  onChange={(file) => {
                    field.onChange(file);
                  }}
                  initialImage={enhancedInitialData.photoUrl ?? null}
                  required={false}
                />
                <FormErrorMessage>{error?.message}</FormErrorMessage>
              </FormControl>
            )}
          />

          <HStack spacing={4}>
            <FormControl isInvalid={!!errors.first_name} flex={1}>
              <FormLabel
                color={errors.first_name ? 'status.error' : 'text.secondary'}
                fontSize={'sm'}
                fontFamily={'primary'}
                fontWeight={'normal'}
                mb={'6px'}
              >
                First Name <Asterisk />
              </FormLabel>
              <Input
                bgColor={'#FFFFFF'}
                placeholder="Enter first name"
                {...register('first_name')}
              />
              <FormErrorMessage>{errors.first_name?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.last_name} flex={1}>
              <FormLabel
                color={errors.last_name ? 'status.error' : 'text.secondary'}
                fontSize={'sm'}
                fontFamily={'primary'}
                fontWeight={'normal'}
                mb={'6px'}
              >
                Last Name <Asterisk />
              </FormLabel>
              <Input bgColor={'#FFFFFF'} placeholder="Enter last name" {...register('last_name')} />
              <FormErrorMessage>{errors.last_name?.message}</FormErrorMessage>
            </FormControl>
          </HStack>

          <FormControl isInvalid={!!errors.email}>
            <FormLabel
              color={errors.email ? 'status.error' : 'text.secondary'}
              fontSize={'sm'}
              fontFamily={'primary'}
              fontWeight={'normal'}
              mb={'6px'}
            >
              Email Address <Asterisk />
            </FormLabel>
            <Input
              bgColor={'#FFFFFF'}
              placeholder="Enter your email"
              {...register('email')}
              isDisabled
              isReadOnly
            />
            <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.phone}>
            <FormLabel
              color={errors.phone ? 'status.error' : 'text.secondary'}
              fontSize={'sm'}
              fontFamily={'primary'}
              fontWeight={'normal'}
              mb={'6px'}
            >
              Phone Number
            </FormLabel>
            <Input bgColor={'#FFFFFF'} placeholder="Enter phone number" {...register('phone')} />
            <FormErrorMessage>{errors.phone?.message}</FormErrorMessage>
          </FormControl>

          <HStack spacing={4}>
            <FormControl isInvalid={!!errors.gender} flex={1}>
              <FormLabel
                color={errors.gender ? 'status.error' : 'text.secondary'}
                fontSize={'sm'}
                fontFamily={'primary'}
                fontWeight={'normal'}
                mb={'6px'}
              >
                Gender
              </FormLabel>
              {gendersLoading ? (
                <HStack justify="center" p={4}>
                  <Spinner size="sm" />
                  <Text fontSize="sm" color="text.secondary">
                    Loading genders...
                  </Text>
                </HStack>
              ) : (
                <Controller
                  name="gender"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      options={genderOptions}
                      selectedValue={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                      }}
                      placeholder="Select gender"
                      size="md"
                      isFullWidth
                      isSearchable={false}
                    />
                  )}
                />
              )}
              <FormErrorMessage>{errors.gender?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.country} flex={1}>
              <FormLabel
                color={errors.country ? 'status.error' : 'text.secondary'}
                fontSize={'sm'}
                fontFamily={'primary'}
                fontWeight={'normal'}
                mb={'6px'}
              >
                Country
              </FormLabel>
              {countriesLoading ? (
                <HStack justify="center" p={4}>
                  <Spinner size="sm" />
                  <Text fontSize="sm" color="text.secondary">
                    Loading countries...
                  </Text>
                </HStack>
              ) : (
                <Controller
                  name="country"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      options={countryOptions}
                      selectedValue={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                      }}
                      placeholder="Select country"
                      size="md"
                      isFullWidth
                      isSearchable={true}
                    />
                  )}
                />
              )}
              <FormErrorMessage>{errors.country?.message}</FormErrorMessage>
            </FormControl>
          </HStack>

          <FormControl isInvalid={!!errors.address}>
            <AddressFieldWithMap
              name="address"
              label="Address"
              isRequired={false}
              isGoogleMapsLoaded={isGoogleMapsLoaded}
            />
            <FormErrorMessage>{errors.address?.message}</FormErrorMessage>
          </FormControl>
        </VStack>
      </FormModal>
    </FormProvider>
  );
};

export default AccountEditModal;
